import React, {
  MouseEvent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import CloseIcon from '@mui/icons-material/Close';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import {
  notEmptyString,
} from '@populateui/utils';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import AutoComplete from 'core/components/AutoComplete';
import {
  ChevronDownIcon,
} from 'core/icons';
import type {
  ClaimAuthorizationOption,
} from 'views/EMR/components/Authorizations/utils';
import AuthorizationValue from 'pages/Dashboard/pages/Billing/ClaimForm/AuthorizationSelector/AuthorizationValue';
import MatchedAuthorizationIndicators from 'pages/Dashboard/pages/Billing/ClaimForm/AuthorizationSelector/components/MatchedAuthorizationIndicators';
import SelectorOptionRow from 'pages/Dashboard/pages/Billing/ClaimForm/AuthorizationSelector/components/SelectorOptionRow';
import useAuthorizationNotes from 'pages/Dashboard/pages/Billing/ClaimForm/AuthorizationSelector/hooks';
import type {
  AuthorizationSelectorProps,
  HighlightSource,
  MuiKeyboardEvent,
  SelectorRow,
} from 'pages/Dashboard/pages/Billing/ClaimForm/AuthorizationSelector/types';
import {
  buildDisplayRows,
  findExactOption,
  getSelectorRowKey,
  normalizeAuthorizationNumber,
} from 'pages/Dashboard/pages/Billing/ClaimForm/AuthorizationSelector/utils';

export { AuthorizationValue };
export type {
  AuthorizationSelectionValue,
} from 'pages/Dashboard/pages/Billing/ClaimForm/AuthorizationSelector/types';

export default function AuthorizationSelector({
  id,
  label,
  value,
  options,
  onChange,
  onAddAuthorization,
  className,
  compact = false,
  showDropdownIcon = true,
  inputRef,
}: AuthorizationSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const highlightedRowKey = useRef<string | null>(null);
  const highlightSource = useRef<HighlightSource | null>(null);
  const lastCommittedDraft = useRef<string | null>(null);
  const blurSuppressionToken = useRef(0);
  const closeAuthorizationOptionsRef = useRef<() => void>(() => {});
  const authorizationNumber = value?.authorizationNumber;
  const [draft, setDraft] = useState(authorizationNumber ?? '');
  const displayRows = useMemo(() => buildDisplayRows(draft, options), [draft, options]);
  const matchedDraftOption = findExactOption(options, draft);
  const matchedCommittedOption = findExactOption(options, authorizationNumber);
  const selectedAuthorizationRow = displayRows.find((row) => (
    row.kind === 'authorization'
    && normalizeAuthorizationNumber(draft) === normalizeAuthorizationNumber(authorizationNumber)
    && normalizeAuthorizationNumber(row.option.authorizationNumber)
      === normalizeAuthorizationNumber(authorizationNumber)
  )) ?? null;
  const showMatchIndicators = !isNil(matchedDraftOption);
  const showMatchDot = showMatchIndicators
    && !isNil(matchedCommittedOption)
    && normalizeAuthorizationNumber(draft)
      === normalizeAuthorizationNumber(authorizationNumber);

  const notes = useAuthorizationNotes({
    inputId: id,
    onClose: () => {
      closeAuthorizationOptionsRef.current();
    },
  });

  useEffect(() => {
    setDraft(authorizationNumber ?? '');
    lastCommittedDraft.current = null;
  }, [authorizationNumber]);

  const clearHighlightState = () => {
    highlightedRowKey.current = null;
    highlightSource.current = null;
  };

  const clearBlurSuppression = () => {
    blurSuppressionToken.current = 0;
  };

  const beginBlurSuppression = (): number => {
    const token = blurSuppressionToken.current + 1;
    blurSuppressionToken.current = token;
    return token;
  };

  const scheduleBlurSuppressionReset = (token: number) => {
    window.setTimeout(() => {
      if (blurSuppressionToken.current === token) {
        blurSuppressionToken.current = 0;
      }
    }, 0);
  };

  const closeAuthorizationOptions = () => {
    notes.resetNotes();
    clearHighlightState();
    setIsOpen(false);
  };
  closeAuthorizationOptionsRef.current = closeAuthorizationOptions;

  const resolveCommittedNumber = (rawDraft: string): string | null => {
    const trimmed = rawDraft.trim();
    if (!notEmptyString(trimmed)) {
      return null;
    }

    return findExactOption(options, trimmed)?.authorizationNumber ?? trimmed;
  };

  const commitDraft = () => {
    if (blurSuppressionToken.current > 0) {
      return;
    }

    const resolved = resolveCommittedNumber(draft);
    const current = authorizationNumber ?? '';
    const nextCommitted = resolved ?? '';
    if (nextCommitted === current || lastCommittedDraft.current === nextCommitted) {
      return;
    }

    lastCommittedDraft.current = nextCommitted;
    onChange({
      authorizationNumber: resolved,
    });
  };

  const selectAuthorization = (option: ClaimAuthorizationOption) => {
    const token = beginBlurSuppression();
    clearHighlightState();
    lastCommittedDraft.current = option.authorizationNumber;
    setDraft(option.authorizationNumber);
    onChange({
      authorizationNumber: option.authorizationNumber,
    });
    closeAuthorizationOptions();
    scheduleBlurSuppressionReset(token);
  };

  const selectUseNumber = (typedNumber: string) => {
    const token = beginBlurSuppression();
    clearHighlightState();
    lastCommittedDraft.current = typedNumber;
    setDraft(typedNumber);
    onChange({
      authorizationNumber: typedNumber,
    });
    closeAuthorizationOptions();
    scheduleBlurSuppressionReset(token);
  };

  const selectAddAuthorization = (typedNumber: string) => {
    const token = beginBlurSuppression();
    clearHighlightState();
    onAddAuthorization?.(typedNumber);
    closeAuthorizationOptions();
    scheduleBlurSuppressionReset(token);
  };

  const selectRow = (row: SelectorRow) => {
    if (row.kind === 'use') {
      selectUseNumber(row.typedNumber);
    } else if (row.kind === 'add') {
      selectAddAuthorization(row.typedNumber);
    } else {
      selectAuthorization(row.option);
    }
  };

  const handleRowMouseDown = (event: MouseEvent) => {
    event.preventDefault();
    beginBlurSuppression();
  };

  const matchedIndicators: ReactNode = showMatchIndicators && matchedDraftOption
    ? (
      <MatchedAuthorizationIndicators
        option={matchedDraftOption}
        showMatchDot={showMatchDot && !isFocused}
      />
    )
    : null;

  return (
    <Wrapper id={id} label={label} className={clsx('min-w-0', compact ? 'flex-1' : undefined, className)}>
      <AutoComplete<SelectorRow>
        id={id}
        className="w-full"
        options={displayRows}
        getLabel={(row) => (
          row.kind === 'authorization' ? row.option.authorizationNumber : row.typedNumber
        )}
        value={selectedAuthorizationRow}
        inputValue={draft}
        freeSolo
        disableClearable
        selectOnFocus
        autoHighlight={false}
        filterSelectedOptions={false}
        clearIcon={<CloseIcon fontSize="small" />}
        clearOnBlur={false}
        openOnFocus
        open={isOpen && displayRows.length > 0}
        onOpen={() => {
          if (notes.suppressNextOpen.current) {
            notes.suppressNextOpen.current = false;
          } else {
            setIsOpen(true);
          }
        }}
        onClose={(event, reason) => {
          if (reason === 'blur' && notes.activeNotesRef.current?.contains(
            'relatedTarget' in event && event.relatedTarget instanceof Node
              ? event.relatedTarget
              : null,
          )) {
            return;
          }
          closeAuthorizationOptions();
        }}
        filterOptions={(items) => items}
        isOptionEqualToValue={(option, selected) => (
          typeof option !== 'string'
          && typeof selected !== 'string'
          && option.kind === 'authorization'
          && selected.kind === 'authorization'
          && option.option.authorizationNumber === selected.option.authorizationNumber
        )}
        popupIcon={showDropdownIcon ? <ChevronDownIcon className="!h-5 !w-5 text-gray-500" /> : null}
        additionalEndAdornment={matchedIndicators}
        slotProps={{
          paper: {
            className: 'rounded-md border border-gray-200 bg-white shadow-md',
          },
          popper: {
            className: 'authorization-selector-popper z-[1300] !w-[min(320px,calc(100vw-32px))]',
            placement: 'bottom-start',
          },
          listbox: {
            className: 'max-h-64 overflow-y-auto !p-0',
            style: { maxHeight: 256 },
          },
        }}
        onInputChange={(_event, nextValue, reason) => {
          if (reason === 'input') {
            clearHighlightState();
            clearBlurSuppression();
            lastCommittedDraft.current = null;
            setDraft(nextValue);
          }
        }}
        onKeyDown={(event: MuiKeyboardEvent) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            highlightSource.current = 'keyboard';
          } else if (event.key === 'Tab') {
            commitDraft();
          } else if (event.key === 'Enter') {
            const canSelectHighlighted = highlightSource.current === 'keyboard'
              && notEmptyString(highlightedRowKey.current);
            if (!canSelectHighlighted) {
              event.preventDefault();
              event.defaultMuiPrevented = true;
              commitDraft();
              closeAuthorizationOptions();
            }
          }
        }}
        onChange={(option, reason, event) => {
          if (reason === 'clear'
            && event?.target instanceof Element
            && event.target.closest('.MuiAutocomplete-clearIndicator')) {
            const token = beginBlurSuppression();
            clearHighlightState();
            lastCommittedDraft.current = '';
            setDraft('');
            onChange({ authorizationNumber: null });
            scheduleBlurSuppressionReset(token);
            return;
          }

          if (option && !Array.isArray(option) && typeof option !== 'string') {
            selectRow(option);
          }
        }}
        onHighlightChange={(_event, option, reason) => {
          if (isNil(option)) {
            clearHighlightState();
            return;
          }

          highlightedRowKey.current = getSelectorRowKey(option);
          highlightSource.current = reason === 'keyboard' ? 'keyboard' : 'mouse';

          if (option.kind === 'authorization') {
            notes.activateNotes(
              option.option.authorizationNumber,
              option.option.authorizations.some((authorization) => (
                notEmptyString(authorization.notes)
              )),
            );
          }
        }}
        inputRef={inputRef}
        onFocus={() => {
          setIsFocused(true);
        }}
        onBlur={() => {
          setIsFocused(false);
          if (blurSuppressionToken.current > 0) {
            clearBlurSuppression();
            return;
          }
          commitDraft();
          if (displayRows.length === 0) {
            closeAuthorizationOptions();
          }
        }}
        renderOption={(props, row) => (
          <SelectorOptionRow
            key={getSelectorRowKey(row)}
            row={row}
            listItemProps={props}
            onRowMouseDown={handleRowMouseDown}
            notes={{
              activeNotesOption: notes.activeNotesOption,
              activeNotesRef: notes.activeNotesRef,
              activateNotes: notes.activateNotes,
              cancelHideNotes: notes.cancelHideNotes,
              scheduleHideNotes: notes.scheduleHideNotes,
              setActiveNotesOption: notes.setActiveNotesOption,
              onClose: closeAuthorizationOptions,
              onNotesEscape: notes.handleNotesEscape,
            }}
          />
        )}
      />
    </Wrapper>
  );
}
